import * as React from "react";
import Slider, { Settings } from "react-slick";
import {
  ConstBreakPoint,
  ConstCalc,
} from "src/config/styles_const/styles_const";
import { ClientBookmark } from "src/domain/model/client_bookmark";
import { Customer } from "src/domain/model/customer";
import { TJobCareerWithCustomerID } from "src/domain/repository/job_career_repository";
import BtnLinkBorderSmall from "src/presentation/common/atom/btn/btn_link_border_small";
import SlideNextArrow from "src/presentation/common/atom/slider_arrow/slide_next_arrow";
import SlidePrevArrow from "src/presentation/common/atom/slider_arrow/slide_prev_arrow";
import { CampaignIQa } from "src/presentation/common/molecular/campaign/campaign-qa";
import { CampaignISection } from "src/presentation/common/molecular/campaign/campaign-section-title";
import styled from "styled-components";
import { routeConfig } from "../../../../config/language/ja-JP/presentation/route_text";
import HeadlineSmall from "../../../common/atom/text/headline_small";
import PageWrapper from "../../../common/atom/wrapper/page_wrapper";
import Breadcrumbs from "../../../common/molecular/breadcrumbs/breadcrumbs";
import Progress from "../../../common/organism/progress";
import { TBreadcrumbItem } from "../../../type/common";
import SlideCustomerCard from "../top_page/slide_customer_card";

interface IProps {
  ipoPrepCustomers: Customer[];
  internalControlCustomers: Customer[];
  auditCustomers: Customer[];
  securitiesAuditCustomers: Customer[];
  fundraisingCustomers: Customer[];
  irStrategyCustomers: Customer[];
  investorRelationsCustomers: Customer[];
  materialsCustomers: Customer[];
  globalCustomers: Customer[];
  cfoCustomers: Customer[];
  jobCareersWithCustomerID: TJobCareerWithCustomerID[];
  clientBookmarks: ClientBookmark[];
  isLoading: boolean;
}

// breackpoint は調整必要
const settings: Settings = {
  className: "slider",
  dots: false,
  infinite: false,
  centerMode: false,
  swipeToSlide: true,
  variableWidth: true,
  arrows: true,
  prevArrow: <SlidePrevArrow />,
  nextArrow: <SlideNextArrow />,
  slidesToShow: 4,
  slidesToScroll: 4,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 3,
      },
    },
    {
      breakpoint: 650,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    },

    {
      breakpoint: 450,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

interface ISubCategoryProps {
  title: string;
  moreLink: string;
  customers: Customer[];
  jobCareersWithCustomerID: TJobCareerWithCustomerID[];
  clientBookmarks: ClientBookmark[];
}

// 各サブカテゴリ（IPO準備体制の構築・監査 など）を全幅4枚スライダーで表示する
const SubCategorySlider: React.FC<ISubCategoryProps> = (props) => {
  const {
    title,
    moreLink,
    customers,
    jobCareersWithCustomerID,
    clientBookmarks,
  } = props;

  return (
    <SubCategoryWrapper>
      <SubTitleWrapper>
        <HeadlineSmall>{title}</HeadlineSmall>
        <BtnLinkBorderSmall to={moreLink} target="_blank">
          もっと見る
        </BtnLinkBorderSmall>
      </SubTitleWrapper>

      <SliderWrapper>
        <Slider {...settings}>
          {customers.map((customer, index) => {
            const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
              (_jobCareerWithCustomerID) => {
                return _jobCareerWithCustomerID.customerID === customer.id;
              }
            );
            const jobCareers = jobCareerWithCustomerID
              ? jobCareerWithCustomerID.jobCareers
              : [];
            const isBookmarked: boolean = clientBookmarks.some(
              (clientBookmark: ClientBookmark) => {
                return clientBookmark.customerID === customer.id;
              }
            );

            return (
              <SlideCustomerWrapper
                key={`${customer.id}-${index}`}
                style={{ width: 200 }}
              >
                <SlideCustomerCard
                  customer={customer}
                  jobCareers={jobCareers}
                  isBookmarked={isBookmarked}
                />
              </SlideCustomerWrapper>
            );
          })}
        </Slider>
      </SliderWrapper>
    </SubCategoryWrapper>
  );
};

const ClientIpoCampaignComponent: React.FC<IProps> = (props) => {
  const {
    ipoPrepCustomers,
    internalControlCustomers,
    auditCustomers,
    securitiesAuditCustomers,
    fundraisingCustomers,
    irStrategyCustomers,
    investorRelationsCustomers,
    materialsCustomers,
    globalCustomers,
    cfoCustomers,
    jobCareersWithCustomerID,
    clientBookmarks,
    isLoading,
  } = props;

  const breadcrumbItems: TBreadcrumbItem[] = [
    routeConfig.clientRoot,
    routeConfig.clientIpoCampaign,
  ];

  // 各サブカテゴリ共通で渡す props をまとめる
  const common = { jobCareersWithCustomerID, clientBookmarks };

  const res: JSX.Element = (
    <React.Fragment>
      {isLoading && <Progress canAnimation={false} />}
      <PageWrapper>
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />

        <CampaignIQa
          q="IPO・IR特集"
          a="IPO・IRといっても、上場準備のフェーズ、資金調達の状況、組織体制、株主・投資家とのコミュニケーション設計によって求められる支援内容は様々です。そのため、経験してきた企業規模や業界、上場準備の実務経験、IR戦略の経験によって異なります。"
        />

        <div style={{ width: "100%", marginTop: 70 }} />

        <CampaignISection
          index={"1"}
          title={"IPOに向けた組織づくり・管理体制の整備"}
          text={
            "IPOを目指す企業では、単に売上や利益を伸ばすだけでなく、上場企業として求められる管理体制やガバナンス体制を整える必要があります。経営管理、内部統制、取締役会運営、監査法人・証券会社対応など、上場審査に耐えうる組織づくりを伴走できる人材が求められます。"
          }
        />

        <SubCategorySlider
          title="IPO準備体制の構築"
          moreLink={`/client/scout/customers?q=IPO準備&p=1`}
          customers={ipoPrepCustomers}
          {...common}
        />
        <SubCategorySlider
          title="内部統制、ガバナンス体制"
          moreLink={`/client/scout/customers?q=内部統制&p=1`}
          customers={internalControlCustomers}
          {...common}
        />
        <SubCategorySlider
          title="監査"
          moreLink={`/client/scout/customers?q=監査&p=1`}
          customers={auditCustomers}
          {...common}
        />
        <SubCategorySlider
          title="証券会社、監査法人の対応"
          moreLink={`/client/scout/customers?q=証券会社 監査法人&p=1`}
          customers={securitiesAuditCustomers}
          {...common}
        />

        <NumberSectionWrapper />

        <CampaignISection
          index={"2"}
          title={"IPO準備における資金調達・資本政策"}
          text={
            "IPO準備では、成長投資に必要な資金調達や、上場時の企業価値を見据えた資本政策が重要になります。どのタイミングで、どの投資家から、どの条件で資金を調達するかによって、上場後の株主構成や経営の自由度にも影響が出ます。"
          }
        />

        <SubCategorySlider
          title="資金調達"
          moreLink={`/client/scout/customers?q=資金調達&p=1`}
          customers={fundraisingCustomers}
          {...common}
        />

        <NumberSectionWrapper />

        <CampaignISection
          index={"3"}
          title={"株価を上げるためのIR戦略"}
          text={
            "上場後、企業価値を正しく市場に伝えるためには、単なる決算説明ではなく、投資家に成長ストーリーを理解してもらうIR戦略が重要です。事業の強み、成長余地、収益モデル、中長期戦略をわかりやすく発信し、株式市場からの評価を高めることが求められます。"
          }
        />

        <SubCategorySlider
          title="IR戦略"
          moreLink={`/client/scout/customers?q=IR戦略&p=1`}
          customers={irStrategyCustomers}
          {...common}
        />
        <SubCategorySlider
          title="投資家対応"
          moreLink={`/client/scout/customers?q=投資家対応&p=1`}
          customers={investorRelationsCustomers}
          {...common}
        />
        <SubCategorySlider
          title="説明資料作成"
          moreLink={`/client/scout/customers?q=IR 説明資料&p=1`}
          customers={materialsCustomers}
          {...common}
        />
        <SubCategorySlider
          title="グローバル対応"
          moreLink={`/client/scout/customers?q=IR グローバル&p=1`}
          customers={globalCustomers}
          {...common}
        />

        <NumberSectionWrapper />

        <CampaignISection
          index={"4"}
          title={"IPO・IR領域に強いCxO・経営企画人材"}
          text={
            "IPOやIRは、管理部門だけで完結するものではなく、経営戦略、事業戦略、財務戦略を一体で設計する必要があります。CFO、経営企画、IR責任者など、経営陣と連携しながら企業価値向上を推進できる人材が重要になります。"
          }
        />

        <SubCategorySlider
          title="CFO、経営企画など"
          moreLink={`/client/scout/customers?q=CFO 経営企画&p=1`}
          customers={cfoCustomers}
          {...common}
        />
      </PageWrapper>
    </React.Fragment>
  );

  return res;
};

export default ClientIpoCampaignComponent;

// 各サブカテゴリの上部は30px空ける
const SubCategoryWrapper = styled.div`
  width: 100%;
  margin-top: 30px;
`;

// 番号セクション（②〜④）の上部は90px空ける
const NumberSectionWrapper = styled.div`
  width: 100%;
  margin-top: 90px;
`;

const SubTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  margin-bottom: 15px;
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    margin-bottom: 30px;
  }
`;

const SliderWrapper = styled.div`
  // react-slickの横幅の計算がうまく働いていないのでここで決めている感じ
  max-width: ${ConstCalc.CustomerSlideMaxWidthSp};
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    max-width: ${ConstCalc.CustomerSlideMaxWidthTablet};
  }
  @media screen and (min-width: ${ConstBreakPoint.PcDeskTop}) {
    max-width: ${ConstCalc.CustomerSlideMaxWidthPc};
  }

  // 全幅スライダーを縦に連続させるため、カードがはみ出さないよう
  // 固定heightではなくmin-heightにして下方向へ自然に伸ばす（縦の最小値は担保）
  & .slick-slider {
    min-height: 334px;
  }
`;

const SlideCustomerWrapper = styled.div`
  padding: 5px 15px;
`;



// WEBPACK FOOTER //
// ./src/presentation/component/client/campaign/client_ipo_campaign.tsx